import { Info } from "lucide-react";
import React, { useEffect } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Event } from "@/lib/types";
import { getCurrencySymbol } from "@/lib/currencyUtils";

interface CartSummaryProps {
  vipSeats: string[];
  vipTicketCount?: number;
  standingTickets: number;
  earlyBirdTickets: number;
  vipPrice: number;
  standingPrice: number;
  earlyBirdPrice: number;
  discountPercentage?: number;
  event: Event;
  onTotalChange?: (total: number) => void;
}

export const CartSummary: React.FC<CartSummaryProps> = ({
  vipSeats,
  vipTicketCount = 0,
  standingTickets,
  earlyBirdTickets,
  vipPrice,
  standingPrice,
  earlyBirdPrice,
  discountPercentage = 0,
  event,
  onTotalChange,
}) => {
  // Calculate VIP quantity based on template type
  const vipQuantity = event.template_type === 'no_seating' 
    ? vipSeats.length 
    : vipTicketCount;

  // Base ticket totals (these prices are already tax-inclusive)
  const vipTotal = vipQuantity * vipPrice;
  const standingTotal = standingTickets * standingPrice;
  const earlyBirdTotal = earlyBirdTickets * earlyBirdPrice;
  const subtotal = vipTotal + standingTotal + earlyBirdTotal;
  
  // GST amount (extracted from tax-inclusive prices)
  const inclusiveGstRate = event.inclusive_gst_rate || 0;
  const displayTaxLineItem = event.displaytax_lineitem || 0;
  
  // Calculate GST component by dividing the subtotal by the GST rate
  const gstAmount = inclusiveGstRate > 0 
    ? subtotal / inclusiveGstRate
    : 0;
  
  // Discount calculation (applied to tax-inclusive amount)
  const discountAmount = (subtotal * discountPercentage) / 100;
  const totalAfterDiscount = subtotal - discountAmount;
  
  // Calculate Slay fee
  const slayFeePercentage = event.slay_fee_percentage || 0;
  const slayFeeFixed = event.slay_fee_fixed || 0;
  const slayFeeAmount = (totalAfterDiscount * slayFeePercentage / 100) + slayFeeFixed;
  
  // Final total (prices are already tax-inclusive)
  const total = totalAfterDiscount + slayFeeAmount;

  // Notify parent component of total change
  useEffect(() => {
    console.log('Cart total updated:', {
      subtotal,
      discountAmount,
      totalAfterDiscount,
      slayFeeAmount,
      finalTotal: total,
      currency: event.currency || 'AUD'
    });
    onTotalChange?.(total);
  }, [total, onTotalChange, subtotal, discountAmount, totalAfterDiscount, slayFeeAmount, event.currency]);

  const currencySymbol = getCurrencySymbol(event.currency);

  return (
    <div className="bg-gray-800/50 rounded-lg p-6 space-y-4">
      <h3 className="text-xl font-bold text-white">Order Summary</h3>
      
      {vipQuantity > 0 && (
        <div className="flex justify-between text-gray-200">
          <span>
            VIP Tickets ({vipQuantity})
            {event.template_type === 'no_seating' && vipSeats.length > 0 && 
              ` - Seats: ${vipSeats.join(", ")}`
            }
          </span>
          <span>{currencySymbol}{vipTotal.toFixed(2)}</span>
        </div>
      )}
      
      {standingTickets > 0 && (
        <div className="flex justify-between text-gray-200">
          <span>General Admission ({standingTickets})</span>
          <span>{currencySymbol}{standingTotal.toFixed(2)}</span>
        </div>
      )}
      
      {earlyBirdTickets > 0 && (
        <div className="flex justify-between text-gray-200">
          <span>Early Bird ({earlyBirdTickets})</span>
          <span>{currencySymbol}{earlyBirdTotal.toFixed(2)}</span>
        </div>
      )}

      <div className="flex justify-between text-gray-200">
        <span>Subtotal</span>
        <span>{currencySymbol}{subtotal.toFixed(2)}</span>
      </div>

      {displayTaxLineItem > 0 && inclusiveGstRate > 0 && (
        <div className="flex justify-between text-gray-200">
          <span>Includes GST ({displayTaxLineItem}%)</span>
          <span>{currencySymbol}{gstAmount.toFixed(2)}</span>
        </div>
      )}

      {discountPercentage > 0 && (
        <div className="flex justify-between text-green-400">
          <span>Discount ({discountPercentage}%)</span>
          <span>-{currencySymbol}{discountAmount.toFixed(2)}</span>
        </div>
      )}

      {slayFeeAmount > 0 ? (
        <div className="flex justify-between text-gray-200">
          <span className="flex items-center gap-1">
            Slay it forward fee
            <Popover>
              <PopoverTrigger asChild>
                <Info className="h-4 w-4 text-gray-400 hover:text-white cursor-pointer" />
              </PopoverTrigger>
              <PopoverContent className="w-80">
                <p className="text-sm">
                  We're LGBTQIA+ owned. This fee supports our tech, our team, and the queer community from DJs and drag queens to designers and devs. This fee is non-refundable.
                </p>
              </PopoverContent>
            </Popover>
          </span>
          <span>{currencySymbol}{slayFeeAmount.toFixed(2)}</span>
        </div>
      ) : null}

      <div className="border-t border-gray-600 pt-4">
        <div className="flex justify-between text-white font-bold">
          <span>Total</span>
          <span>{currencySymbol}{total.toFixed(2)}</span>
        </div>
        {slayFeeAmount === 0 && (
          <div className="text-[#9b87f5] text-sm mt-2 italic">
            Free booking fees! {event.producer_name} has covered it for you 🎉
          </div>
        )}
      </div>
    </div>
  );
};
